/**
 * Onboarding state hook.
 *
 * Fetches GET /api/onboarding/state once on mount + on every active-workspace
 * change. Exposes a `markComplete()` helper for step 5 + a `skip()` helper
 * for the "skip onboarding" affordance.
 *
 * Failure modes: when the state call fails (backend down, no workspace
 * yet), we return `needsOnboarding=false` so the SPA never redirects into
 * a wizard it can't drive. The error is surfaced via `error` so callers
 * can opt to retry.
 */
import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import { useActiveWorkspace } from '../runtime/ActiveWorkspaceContext';
import type { OnboardingState } from '../types';

export interface UseOnboardingResult {
  state: OnboardingState | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  skip: () => Promise<void>;
  markComplete: () => Promise<void>;
  reset: () => Promise<void>;
}

const FALLBACK: OnboardingState = {
  needsOnboarding: false,
  currentStep: 0,
  onboarded: false,
  counts: { flows: 0, connections: 0, eventDesigns: 0 },
};

export function useOnboarding(): UseOnboardingResult {
  const { active } = useActiveWorkspace();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await api.onboardingState();
      setState(s);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'fetch failed';
      setError(msg);
      setState(FALLBACK);
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when the active workspace changes — onboarding is per-workspace.
  useEffect(() => {
    void refresh();
  }, [refresh, active?.slug]);

  const skip = useCallback(async () => {
    try {
      await api.onboardingSkip();
    } finally {
      await refresh();
    }
  }, [refresh]);

  const markComplete = useCallback(async () => {
    try {
      await api.onboardingComplete();
    } finally {
      await refresh();
    }
  }, [refresh]);

  const reset = useCallback(async () => {
    try {
      await api.onboardingReset();
    } finally {
      await refresh();
    }
  }, [refresh]);

  return { state, loading, error, refresh, skip, markComplete, reset };
}
